<template>
  <v-sheet elevation="2" class="px-12 py-8 rounded white">
    <div class="mb-6">
      <p class="h6 pa-0 mb-0">
        {{ $t('common.category_other') }}
      </p>
      <p
        class="mt-2 pa-0 grey--text text--darken-2"
        v-html="$t('organization_settings.categories_subtitle')"
      ></p>
    </div>
    <v-form ref="categories">
      <v-row>
        <v-col cols="5">
          <v-select
            outlined
            required
            dense
            item-text="text"
            data-testid="categories-select-category"
            item-value="id"
            v-model="ruleToAdd.category"
            :label="`${$t('common.category_other')}*`"
            :items="categoriesOptions"
            :placeholder="$t('common.select')"
          ></v-select
        ></v-col>
        <v-col cols="5">
          <v-select
            outlined
            required
            dense
            item-text="text"
            item-value="isVisible"
            data-testid="categories-select-status"
            v-model="ruleToAdd.isVisible"
            :label="`${$t('common.status')}*`"
            :placeholder="$t('common.select')"
            :items="status"
          ></v-select>
        </v-col>
        <v-col cols="2">
          <Button
            size="large"
            color="primary"
            data-testid="categories-button-add"
            :loading="isLoading"
            :disabled="shouldDisableAddRuleButton"
            :text="$t('common.button.add')"
            @click="createCategoryRule"
          />
        </v-col>
      </v-row>
    </v-form>
    <div class="mt-4" v-if="rules.length">
      <p class="h6 mb-3 mt-0">
        {{ $t('organization_settings.added_rules') }}
      </p>
      <template v-for="(rule, index) in rules">
        <v-hover v-slot:default="{ hover }" :key="rule.id">
          <div
            v-if="rule.isViewMode"
            data-testid="categories-div-addedRules"
            class="pa-2 d-flex justify-space-between align-center"
            :class="{
              hover: hover && rule.isViewMode,
              grey: hover && rule.isViewMode,
              'lighten-5': hover && rule.isViewMode,
            }"
          >
            <p
              class="ma-0"
              :data-testid="`categories-paragraph-addedRules${index}`"
            >
              {{ rule.text }}
            </p>
            <div class="d-flex align-center justify-space-between">
              <v-icon
                v-for="icon in view_mode_icons"
                class="grey--text icon"
                :data-testid="`categories-icon-${icon.type}${index}`"
                :key="icon.type"
                :disabled="disableActions"
                @click="handleClick(icon.type, rule)"
              >
                {{ icon.name }}
              </v-icon>
            </div>
          </div>
          <div v-else class="pa-2 d-flex justify-space-between align-center">
            <v-text-field
              outlined
              dense
              hide-details
              disabled
              class="flex-grow-1 flex-shrink-0"
              :data-testid="`categories-textField-category${index}`"
              :value="$t(`common.categories.${rule.category.name}`)"
              :label="$t('common.category_one')"
            ></v-text-field>
            <v-select
              outlined
              dense
              hide-details
              class="mx-4"
              item-text="text"
              item-value="isVisible"
              v-model="rule.isVisible"
              :items="status"
              :data-testid="`categories-select-statusEdit${index}`"
              :label="$t('common.status')"
            ></v-select>
            <div class="d-flex me-5">
              <v-icon
                v-for="icon in edit_mode_icons"
                class="grey--text icon"
                :data-testid="`categories-icon-${icon.type}${index}`"
                :loading="isSaving"
                :key="icon.type"
                @click="handleClick(icon.type, rule)"
              >
                {{ icon.name }}
              </v-icon>
            </div>
          </div>
        </v-hover>
      </template>
    </div>
    <DeleteConfirmationModal
      confirmTestID="categories-delete-button"
      :show="showDeleteModal"
      :isLoading="isLoading"
      :title="$t('organization_settings.delete_rule_title')"
      :text="$t('organization_settings.delete_confirmation')"
      @cancel="showDeleteModal = false"
      @delete="removeRule"
    />
  </v-sheet>
</template>
<script>
import mixin from '@/mixins/mixin.vue'
import { mapActions } from 'vuex'
import _ from 'lodash'

import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'Categories',

  mixins: [mixin],

  components: {
    DeleteConfirmationModal,
    Button,
  },

  props: {
    organizationId: {
      required: true,
      type: String,
    },
  },

  data() {
    return {
      isSaving: false,
      isLoading: true,
      showDeleteModal: false,
      categoriesCatalog: [],
      ruleToAdd: { category: '', isVisible: null },
      rules: [],
      disableActions: false,
      status: [
        {
          isVisible: true,
          text: this.$t('organization_settings.enabled_for_all'),
        },
        {
          isVisible: false,
          text: this.$t('organization_settings.disabled_for_all'),
        },
      ],
      view_mode_icons: [
        { type: 'edit', name: 'create' },
        { type: 'delete', name: 'mdi-delete-outline' },
      ],
      edit_mode_icons: [
        { type: 'save', name: 'done' },
        { type: 'cancel', name: 'clear' },
      ],
    }
  },

  async mounted() {
    await this.getCategoriesCatalog()
    await this.getOrganizationRules()
  },

  computed: {
    categoriesOptions() {
      return this.categoriesCatalog.filter(this.getCategoriesWithoutRule)
    },
    shouldDisableAddRuleButton() {
      return (
        this.isLoading ||
        !this.ruleToAdd.category ||
        this.ruleToAdd.isVisible === null
      )
    },
  },

  methods: {
    ...mapActions({
      getCatalog: 'categories/getOrganizationCatalog',
      getRules: 'categories/getOrganizationRules',
      createRule: 'categories/createOrganizationRule',
      updateRule: 'categories/updateOrganizationRule',
      deleteRule: 'categories/deleteOrganizationRule',
    }),

    getCategoriesWithoutRule(category) {
      const categoriesIds = this.rules.map(rule => rule.category.id)
      return !categoriesIds.includes(category.id)
    },

    async removeRule() {
      try {
        this.isLoading = true
        await this.deleteRule({
          organizationId: this.organizationId,
          ruleId: this.ruleToDelete.id,
        })
        this.showSuccess(
          this.$t('organization_settings.rule_removed_successfully')
        )
        await this.getOrganizationRules()
        this.showDeleteModal = false
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },
    async getCategoriesCatalog() {
      try {
        this.isLoading = true
        const response = await this.getCatalog({
          organizationId: this.organizationId,
        })
        const catalog = response
        this.categoriesCatalog = this.formatCategories(catalog)
        await this.getOrganizationRules()
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async getOrganizationRules() {
      try {
        this.isLoading = true

        const response = await this.getRules({
          organizationId: this.organizationId,
        })

        this.rules = response.data
        this.rules = this.formatRules()
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async createCategoryRule() {
      this.isLoading = true
      try {
        await this.createRule({
          organizationId: this.organizationId,
          rule: this.ruleToAdd,
        })
        this.ruleToAdd = { category: '', isVisible: null }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
        this.getOrganizationRules()
      }
    },

    async updateCategoryRule(rule) {
      try {
        this.isSaving = true
        await this.updateRule({
          organizationId: this.organizationId,
          ruleId: rule.id,
          rule,
        })
      } catch (err) {
        this.handleRequestError(err)
        rule.isVisible = !rule.isVisible
      } finally {
        this.isSaving = false
        this.disableActions = false
        this.getOrganizationRules()
      }
    },

    handleClick(type, rule) {
      switch (type) {
        case 'edit':
          this.disableActions = true
          this.ruleToEdit = rule
          rule.isViewMode = false
          break
        case 'delete':
          this.ruleToDelete = rule
          this.showDeleteModal = true
          break
        case 'save':
          this.updateCategoryRule(rule)
          break
        case 'cancel':
          this.isSaving = true
          rule = this.ruleToEdit
          rule.isViewMode = true
          this.disableActions = false
          this.isSaving = false
          break
      }
    },

    formatCategories(catalog) {
      const categories = _.chain(catalog)
        .map(category => ({
          ...category,
          text: this.$t(`common.categories.${category.name}`),
        }))
        .orderBy('text', 'asc')
        .value()

      const other = _.remove(categories, c => c.name === 'other')
      categories.push(other[0])

      return categories
    },

    formatRules() {
      const rules = _.chain(this.rules)
        .map(rule => ({
          ...rule,
          isViewMode: true,
          text: `${this.$t(`common.categories.${rule.category.name}`)} - ${
            rule.isVisible
              ? _.capitalize(this.$t('organization_settings.enabled_for_all'))
              : _.capitalize(this.$t('organization_settings.disabled_for_all'))
          }`,
        }))
        .orderBy('text', 'asc')
        .value()

      const other = _.remove(rules, rule => rule.category.name === 'other')
      if (other[0]) rules.push(other[0])

      return rules
    },
  },
}
</script>
<style lang="scss" scoped>
.hover {
  border-radius: 4px;
}
.icon {
  &:last-child:not(:first-child) {
    margin-left: 20px !important;
  }
}
</style>
